import { useMemo, useCallback, useRef, useState } from 'react'
import { Editor } from '@tinymce/tinymce-react'
import clsx from 'clsx'

import { useArtifactUpload } from './use-artifact-upload'
import { newEditorToken, reportEditorUploading } from './uploading-editors'
import { setupVideoEditor, type VideoEditor } from './editor-video-setup'
import './styles.css'

interface TinyMceEditorProps {
	value: string
	onChange: (value: string) => void
	onBlur?: () => void
	id?: string
	disabled?: boolean
	height?: number
	/**
	 * Only applies to 'compact'. Defaults to true — the editor grows with content
	 * (autoresize plugin), no cap, matching existing modal usages (invites,
	 * reminders, contact messages). Set to false for a fixed height that scrolls
	 * internally instead, for layouts with limited vertical room where unbounded
	 * growth would push surrounding content out of view (e.g. the inbox reply composer).
	 */
	autoResize?: boolean
	placeholder?: string
	className?: string
	/** Called when image upload state changes — use to disable save buttons while uploading. */
	onUploadingChange?: (isUploading: boolean) => void
	/** Show a red error border around the editor. */
	error?: boolean
	/**
	 * 'full' (default) is the whole toolbar/menubar, sized for a full-page field
	 * (event description/notes/terms/FAQs). 'compact' drops the menubar, statusbar,
	 * and block/font-size pickers down to a single slim toolbar row — for short
	 * messages composed inside a modal (invites, reminders, contact messages).
	 */
	variant?: 'full' | 'compact'
}

const FONT_FAMILY_FORMATS =
	'Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Cormorant Upright=cormorant upright; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Roboto=roboto,sans-serif; Symbol=symbol; Nunito=Nunito; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats'

function getContentStyle(paddingY: number, paddingX: number) {
	return `
  @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap');

  html, body, .mce-content-body {
    scrollbar-width: none !important;
  }

  html::-webkit-scrollbar,
  body::-webkit-scrollbar,
  .mce-content-body::-webkit-scrollbar {
    width: 0 !important;
    height: 0 !important;
    display: none !important;
  }

  body {
    margin: 0;
    background-color: rgb(61 62 77 / 0.4);
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    line-height: 1.2;
    outline: none !important;
    box-shadow: none !important;
    padding: ${paddingY}px ${paddingX}px;
  }

  /* The cursor sits inside the first paragraph, not the body box — without
     zeroing its margin, the browser's default p { margin } pushes typed text
     down from the padding edge while the placeholder below stays pinned to it. */
  body > *:first-child {
    margin-top: 0 !important;
  }

  .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
    position: absolute;
    top: ${paddingY}px;
    left: ${paddingX}px !important;
    right: ${paddingX}px;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    color: #b7bac6;
    font-style: italic;
    font-weight: 300;
    opacity: 0.8;
    /* Without this, right:${paddingX}px narrows the box enough that longer
       placeholders wrap onto a second line, which then clips out of view in a
       fixed (non-autoresize) height instead of growing to fit like autoresize would. */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  a {
    color: #7c8cf8;
  }
`
}

const PLUGINS = [
	'preview',
	'searchreplace',
	'autolink',
	'autosave',
	'directionality',
	'code',
	'visualblocks',
	'visualchars',
	'fullscreen',
	'image',
	'link',
	'media',
	'codesample',
	'table',
	'charmap',
	'emoticons',
	'pagebreak',
	'nonbreaking',
	'anchor',
	'insertdatetime',
	'advlist',
	'lists',
	'wordcount',
	'help',
	'quickbars',
]

const TOOLBAR = `
  undo redo | blocks fontfamily fontsize |
  bold italic underline strikethrough |
  alignleft aligncenter alignright alignjustify |
  outdent indent |
  numlist bullist |
  link image video table |
  charmap emoticons |
  insertdatetime |
  forecolor backcolor removeformat |
  code preview
`

// Compact: one slim row, no menubar/statusbar — just enough for a short message
// (bold/italic/underline, a link, an image, lists, emoji). Grows with content
// (autoresize plugin) unless autoResize=false, in which case it's a fixed height
// that scrolls internally instead, like 'full'.
const COMPACT_PLUGINS_BASE = ['autolink', 'link', 'image', 'lists', 'emoticons', 'wordcount']
const COMPACT_TOOLBAR = 'bold italic underline | bullist numlist | link image | emoticons | removeformat'

function TinyMceEditor({
	value,
	onChange,
	onBlur,
	id,
	disabled = false,
	height,
	autoResize = false,
	placeholder,
	className,
	onUploadingChange,
	error = false,
	variant = 'full',
}: TinyMceEditorProps) {
	// Reported by the upload hook itself when an upload starts and settles, into a set shared by every editor
	// using the same callback. An editor unmounted mid-upload therefore stays "uploading" until it settles.
	const [editorId] = useState(newEditorToken)
	const onUploadingChangeRef = useRef(onUploadingChange)
	onUploadingChangeRef.current = onUploadingChange
	const reportUploading = useCallback(
		(uploading: boolean) => {
			if (onUploadingChangeRef.current) reportEditorUploading(onUploadingChangeRef.current, editorId, uploading)
		},
		[editorId]
	)
	const { imagesUploadHandler, uploadVideo, maxVideoSizeMb, videoAccept, setContentGetter } =
		useArtifactUpload(reportUploading)
	// The editor is set up once, so reach the latest upload function through a ref rather than a stale closure.
	const uploadVideoRef = useRef(uploadVideo)
	uploadVideoRef.current = uploadVideo
	const isCompact = variant === 'compact'
	const usesAutoResize = isCompact && autoResize
	const resolvedHeight = height ?? (isCompact ? 260 : 500)

	const handleInit = useCallback(
		(_evt: unknown, editor: { getContent: () => string }) => {
			setContentGetter(() => editor.getContent())
		},
		[setContentGetter]
	)

	const init = useMemo(
		() => ({
			// autoResize (compact only): min_height sets the floor, no cap, so it
			// never scrolls internally. Otherwise: fixed height, scrolls internally
			// once content overflows it. Full also gets a manual resize handle (below).
			...(usesAutoResize ? { min_height: resolvedHeight } : { height: resolvedHeight }),
			placeholder,
			skin: 'oxide-dark' as const,
			content_css: 'dark',
			plugins: isCompact ? [...COMPACT_PLUGINS_BASE, ...(usesAutoResize ? ['autoresize'] : [])] : PLUGINS,
			toolbar: isCompact ? COMPACT_TOOLBAR : TOOLBAR,
			toolbar_mode: 'sliding' as const,
			menubar: isCompact ? false : ('view insert format' as const),
			// Without an explicit `theme`, TinyMCE swaps in its own mobile theme on touch
			// viewports, which only supports a horizontally-scrolling toolbar and ignores
			// toolbar_mode entirely. Forcing 'silver' keeps the same sliding overflow menu.
			mobile: {
				theme: 'silver' as const,
				menubar: isCompact ? false : ('view insert format' as const),
				toolbar_mode: 'sliding' as const,
			},
			statusbar: !isCompact,
			content_style: isCompact ? getContentStyle(12, 18) : getContentStyle(16, 20),
			font_family_formats: FONT_FAMILY_FORMATS,
			valid_elements: '*[*]',
			extended_valid_elements:
				'iframe[src|frameborder|style|allowfullscreen|title|width|height|allow|referrerpolicy],button[onclick]',
			// Default inserted links to opening in a new tab rather than navigating
			// away from wherever this content ends up rendered.
			link_default_target: '_blank',
			media_live_embeds: true,
			branding: false,
			resize: !isCompact,
			...(usesAutoResize && { autoresize_bottom_margin: 8 }),
			...(!isCompact && {
				quickbars_selection_toolbar: 'bold italic underline | quicklink h2 h3 blockquote',
				quickbars_insert_toolbar: 'quickimage quicktable',
			}),
			images_upload_handler: imagesUploadHandler,
			setup: (editor: VideoEditor) =>
				setupVideoEditor(editor, {
					// The editor is set up once, so reach the latest upload function through a ref.
					uploadVideo: (file, onProgress) => uploadVideoRef.current(file, onProgress),
					maxSizeMb: maxVideoSizeMb,
					accept: videoAccept,
					isCompact,
				}),
			...(!isCompact && {
				menu: {
					insert: {
						title: 'Insert',
						items: 'image link video codesample inserttable | charmap emoticons hr | pagebreak nonbreaking anchor | insertdatetime',
					},
				},
			}),
			automatic_uploads: true,
			images_reuse_filename: true,
			browser_spellcheck: true,
			contextmenu: false,
		}),
		[resolvedHeight, placeholder, isCompact, usesAutoResize, imagesUploadHandler, maxVideoSizeMb, videoAccept]
	)

	return (
		<div
			className={clsx(
				'tinymce-rte-wrapper',
				{ 'tinymce-rte-disabled': disabled, 'tinymce-rte-error': error, 'tinymce-rte-compact': isCompact },
				className
			)}
		>
			<Editor
				// @tinymce/tinymce-react only calls tinymce.init() on mount — it never
				// re-initialises an already-running editor when `init` changes (e.g.
				// min_height/height). Keying on the values that matter forces a clean
				// remount whenever they actually change.
				key={`${variant}-${resolvedHeight}-${usesAutoResize}`}
				id={id}
				tinymceScriptSrc="https://cdn.jsdelivr.net/npm/tinymce@7/tinymce.min.js"
				value={value}
				onEditorChange={onChange}
				onBlur={onBlur}
				onInit={handleInit}
				disabled={disabled}
				init={init}
			/>
		</div>
	)
}

export { TinyMceEditor }
export type { TinyMceEditorProps }
